(() => {
  const TARGET_SELECTOR = '[data-product-trustpilot-title]';
  const SCORE_SELECTOR = '[data-product-trustpilot-rating]';

  const productFromStructuredData = (value) => {
    if (Array.isArray(value)) {
      for (const item of value) {
        const product = productFromStructuredData(item);
        if (product) return product;
      }
      return null;
    }

    if (!value || typeof value !== 'object') return null;

    const types = Array.isArray(value['@type']) ? value['@type'] : [value['@type']];
    if (types.includes('Product') && value.aggregateRating?.ratingValue != null) {
      return value;
    }

    if (Array.isArray(value['@graph'])) {
      return productFromStructuredData(value['@graph']);
    }

    return null;
  };

  const readProductRating = () => {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');

    for (const script of scripts) {
      try {
        const product = productFromStructuredData(JSON.parse(script.textContent));
        const rating = Number.parseFloat(product?.aggregateRating?.ratingValue);
        const reviewCount = Number.parseInt(
          product?.aggregateRating?.reviewCount ?? product?.aggregateRating?.ratingCount,
          10
        );

        if (Number.isFinite(rating) && rating > 0) {
          return {
            rating,
            reviewCount: Number.isFinite(reviewCount) && reviewCount >= 0 ? reviewCount : null,
          };
        }
      } catch (error) {
        // Ignore structured data belonging to other integrations.
      }
    }

    return null;
  };

  const applyProductRating = () => {
    const targets = document.querySelectorAll(TARGET_SELECTOR);
    if (!targets.length) return false;

    const reviewSummary = readProductRating();
    if (reviewSummary == null) return false;

    const { rating, reviewCount } = reviewSummary;

    const formattedRating = Number.isInteger(rating) ? rating.toFixed(0) : rating.toFixed(1);

    targets.forEach((target) => {
      const score = target.querySelector(SCORE_SELECTOR);
      const scale = target.querySelector('[data-product-trustpilot-scale]');
      const reviews = target.querySelector('[data-product-trustpilot-reviews]');
      const stars = target.querySelector('[data-product-trustpilot-stars]');
      const summary = target.querySelector('[data-product-trustpilot-summary]');
      const empty = target.querySelector('[data-product-trustpilot-empty]');
      if (!score || !scale || !stars || !summary) return;

      target.querySelectorAll('[data-product-trustpilot-star]').forEach((star, index) => {
        const fill = Math.max(0, Math.min(1, rating - index)) * 100;
        star.style.setProperty('--trustpilot-star-fill', `${fill}%`);
      });

      score.textContent = formattedRating;
      summary.hidden = false;
      if (empty) empty.hidden = true;

      if (reviews && reviewCount != null) {
        reviews.textContent = `${reviewCount.toLocaleString('en-GB')} ${
          reviewCount === 1 ? 'review' : 'reviews'
        }`;
        reviews.hidden = false;
      }

      target.classList.add('is-score-ready');
      target.classList.remove('is-empty');
      summary.setAttribute(
        'aria-label',
        `Rated ${formattedRating} out of 5 on Trustpilot${
          reviewCount == null ? '' : `, based on ${reviewCount} ${reviewCount === 1 ? 'review' : 'reviews'}`
        }. Read customer reviews.`
      );
    });

    return true;
  };

  const applyEmptyState = () => {
    const targets = document.querySelectorAll(TARGET_SELECTOR);
    if (!targets.length) return false;

    targets.forEach((target) => {
      const summary = target.querySelector('[data-product-trustpilot-summary]');
      const empty = target.querySelector('[data-product-trustpilot-empty]');
      if (!empty) return;

      target.classList.add('is-empty');
      target.classList.remove('is-score-ready');
      if (summary) summary.hidden = true;
      empty.hidden = false;
    });

    return true;
  };

  const initialise = () => {
    if (applyProductRating()) return;

    const observer = new MutationObserver(() => {
      if (applyProductRating()) {
        window.clearTimeout(emptyStateTimer);
        observer.disconnect();
      }
    });
    const emptyStateTimer = window.setTimeout(() => {
      if (!applyProductRating()) applyEmptyState();
    }, 2200);

    observer.observe(document.documentElement, { childList: true, subtree: true });
    window.setTimeout(() => {
      observer.disconnect();
      window.clearTimeout(emptyStateTimer);
      if (!applyProductRating()) applyEmptyState();
    }, 12000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-product-trustpilot-scroll]');
    if (!trigger) return;

    const reviews =
      document.querySelector('.product-reviews') ??
      document.querySelector('[data-template-id="5763bccae0a06d08e809ecbb"]');

    if (reviews) {
      reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

  document.addEventListener('shopify:section:load', initialise);
  document.addEventListener('trustpilot:product-summary:ready', initialise);
})();
